import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

import { isCommissionDebtLimitError, isRetryableError, normalizeApiError } from "@/lib/api/errors";
import { notify } from "@/stores/notification.store";
import { UNAVAILABLE_STATUSES } from "@/stores/serviceStatus.store";

declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: { silentError?: boolean };
    mutationMeta: { silentError?: boolean };
  }
}

// 401 is handled by the auth interceptor, 402 by the commission gate modal, and
// offline / 5xx-gateway failures by the full-screen AppStatusOverlay.
const isHandledElsewhere = (error: unknown) => {
  const { kind, status } = normalizeApiError(error);
  if (kind === "canceled" || status === 401 || isCommissionDebtLimitError(error)) return true;
  if (kind === "network" && !navigator.onLine) return true;
  return status !== null && UNAVAILABLE_STATUSES.includes(status);
};

// Pages render their own empty/not-found state for 4xx query failures, so only
// failures the page cannot explain (offline, server down, forbidden) reach a toast.
const shouldToastQueryError = (error: unknown) => {
  const { kind, status } = normalizeApiError(error);
  if (kind !== "http") return true;
  return status !== null && (status >= 500 || status === 403 || status === 429);
};

const MAX_QUERY_RETRIES = 2;

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.silentError || isHandledElsewhere(error)) return;
      if (!shouldToastQueryError(error)) return;
      notify(normalizeApiError(error).message, "error");
    },
  }),

  // A mutation that passes its own onError shows the error inline, so the global
  // toast only covers mutations that would otherwise fail silently.
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.onError || mutation.meta?.silentError) return;
      if (isHandledElsewhere(error)) return;
      notify(normalizeApiError(error).message, "error");
    },
  }),

  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      retry: (failureCount, error) => failureCount < MAX_QUERY_RETRIES && isRetryableError(error),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      networkMode: "online",
    },

    mutations: {
      // Retrying a POST can duplicate an order or burn an OTP attempt.
      retry: false,
    },
  },
});
